import type { ReactNode } from 'react';
import { AXIS_COLOURS } from './meshAnnotations';

// THREE COLUMNS, ONE PER ARM OF THE GIZMO, WITH TITLED ROWS OF NUMBERS UNDER THEM.
//
// The shape two panels in this phase are now drawn in: the viewport corner, which reads out
// where the CAMERA stands and how it is turned, and the card panel, which reads out — and
// edits — where the PART stands and how it is turned. The same three axes, the same two
// questions about them, in the same frame; the only difference is whose numbers they are.
//
// SO IT IS ONE GRID AND NOT TWO THAT RESEMBLE EACH OTHER. Both used to carry their own copy
// of the three hex colours (the part panel had them written out as Tailwind arbitrary values,
// which is the same numbers a second time and no way to tell they were meant to match), their
// own letter row and their own caption style. Anything read off one and compared with the
// other depends on those agreeing, and two copies of a colour agree until somebody edits one.
//
// AND WHAT GOES IN THE CELLS IS NOW HERE TOO, because both panels put the same thing there: a
// field you type a number into. The corner used to put buttons there — each reading opened a
// menu of steps, "+10", "+90" — and a step is the wrong instrument for arriving somewhere
// exact. Typing an angle says where to be; pressing +90 twice says how to get there and only
// from where you already are.

export const AXES = (['x', 'y', 'z'] as const).map((axis) => ({
  axis,
  colour: `#${AXIS_COLOURS[axis].toString(16).padStart(6, '0')}`,
}));

// CENTIMETRES, WHICH IS WHAT EVERY GRID DRAWN THIS WAY IS READ IN. Underneath, this phase is
// millimetres throughout — the seat fields, the travel, the measurement lines, the scene's own
// units — and it stays that way; the conversion belongs at the edge, where a person reads a
// distance at arm's length against a gizmo whose arms are a centimetre each.
//
// Two decimals, because a millimetre is 0.1 cm and half of one still moves a fitting.
export const MM_PER_CM = 10;
export const asCm = (mm: number) => Math.round((mm / MM_PER_CM) * 100) / 100;

// WHAT SOMEBODY TYPED, AS A NUMBER. A comma is a decimal point: every grid drawn this way is
// read in French as often as in English, and "1,5" typed into a box that answers to a full
// stop is a part sent to 15 or to 0 with nothing said about it.
export const asNumber = (typed: string) => Number(typed.replace(',', '.')) || 0;

// The letters, once, over the columns they name. Coloured like the arms they stand for: three
// single letters in one grey are a legend you have to read, and in the gizmo's own three they
// are the arrow you are already looking at.
export function AxisLetters({ cell }: { cell: string }) {
  return (
    <div className="flex">
      {AXES.map(({ axis, colour }) => (
        <span
          key={axis}
          style={{ color: colour }}
          className={`${cell} text-[11px] font-bold leading-none`}
        >
          {axis.toUpperCase()}
        </span>
      ))}
    </div>
  );
}

// WHAT THE ROW UNDER IT MEASURES, AND IN WHAT UNIT — "camera position (cm)", "part
// orientation (°)". Over the row rather than beside each number, which is what decides the
// whole layout: a unit written three times is the same word lined up with nothing.
//
// AND, GIVEN AN `onClick`, IT IS ALSO THE SWITCH BETWEEN ITS ROW AND THE OTHER ONE.
//
// The camera corner's two rows are that switch — a drag either aims the shot or swings it
// round. Everywhere else, the part panel included, it is the plain caption.
// THREE NUMBERS YOU CAN TYPE INTO, in the columns' own colours — one row of the grid.
//
// A LIVE READING AND AN INSTRUCTION AT ONCE, which is what decides how it commits. The values
// follow the thing they measure while nobody is typing — a part being dragged, a camera being
// orbited — and the caller stops feeding them the moment a key is pressed, because up to the
// commit the half-typed number is the typist's. That is also why these are text fields rather
// than number inputs: a spinner applies as you touch it, and a half-typed number is not a
// place anybody meant.
//
// LEAVING THE FIELD IS THE COMMIT, Enter is leaving it early, and Escape is the way out of a
// number you have started and thought better of. The whole row goes at once — three numbers
// are one answer about where something is — so tabbing from X to Y moves the thing once
// rather than banking a change nobody applied.
export function AxisFields({ values, cell, onChange, onCommit, onRevert }: {
  values: string[];
  cell: string;
  onChange: (next: string[]) => void;
  onCommit: (next: string[]) => void;
  onRevert: () => void;
}) {
  return (
    <div className="flex">
      {AXES.map(({ axis, colour }, i) => (
        // The breathing room is INSIDE the cell rather than a gap between cells, so a row of
        // fields is exactly as wide as the row of letters over it. A `gap` would push the
        // columns apart from the heading they are named by, which is the one thing this grid
        // exists to keep true.
        <div key={axis} className={`${cell} px-0.5`}>
          <input
            type="text"
            inputMode="decimal"
            value={values[i]}
            style={{ color: colour }}
            onChange={(e) => onChange(values.map((v, k) => (k === i ? e.target.value : v)))}
            onBlur={() => onCommit(values)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onCommit(values);
              if (e.key === 'Escape') onRevert();
            }}
            className="w-full min-w-0 rounded border border-slate-300 bg-transparent px-0.5 py-0.5 text-center text-[10px] font-bold leading-none tabular-nums"
          />
        </div>
      ))}
    </div>
  );
}

// A heading in ink: it names the three numbers under it, and nothing else says which row is which.
export function AxisRowTitle({ children }: { children: ReactNode }) {
  return <p className="mt-1 w-full text-center text-[9px] font-bold uppercase tracking-wide text-slate-900">{children}</p>;
}
